import { FormControl, InputLabel } from '@mui/material'
import Container from '@mui/material/Container'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { GetCompaniesByNameQuery } from '__generated__/graphql'
import IntakeCard from 'candidate/components/IntakeCard'
import React, { useEffect, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import NextButton from 'shared/components/NextButton'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import SecondaryButton from 'shared/components/SecondaryButton'
import TertiaryButton from 'shared/components/TertiaryButton'
import { getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'
import { AdditionalButtonsContainer } from '../styles'

export interface ReferenceAnswer {
    id: number
    firstName: string
    lastName: string
    company: GetCompaniesByNameQuery['companiesByName']['data'][number] | null
    workTitle: string
    email: string
    phone: string
    error?: string
}
interface Props {
    answer?: ReferenceAnswer[]
    callback: React.Dispatch<any>
    workTitles: string[]
    prompt: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const ReferenceQuestion: React.FC<Props> = ({
    answer,
    callback,
    workTitles,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const blankEntry: ReferenceAnswer = React.useMemo(
        () => ({
            id: 1,
            firstName: '',
            lastName: '',
            company: null,
            workTitle: '',
            email: '',
            phone: '',
            error: '',
        }),
        []
    )

    const [entries, setEntries] = useState<ReferenceAnswer[]>([blankEntry])
    const [disabled, setDisabled] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(true)

    // Initialize entries with the answer if it exists
    useEffect(() => {
        if (answer) {
            if (answer.length > 0) {
                const allAnswers: ReferenceAnswer[] = []
                answer.map((entry) => {
                    const ans: ReferenceAnswer = {
                        id: entry.id,
                        firstName: entry.firstName,
                        lastName: entry.lastName,
                        company: entry.company,
                        workTitle: entry.workTitle,
                        email: entry.email,
                        phone: entry.phone,
                        error: '',
                    }
                    allAnswers.push(ans)
                    return ans
                })
                setEntries(allAnswers)
            } else {
                setEntries([blankEntry])
            }
            setLoading(false)
        } else {
            setLoading(false)
        }
    }, [answer, blankEntry])

    // Add a new entry
    const addEntry = () => {
        setEntries([
            ...entries,
            {
                id: entries.length + 1,

                firstName: '',
                lastName: '',
                company: null,
                workTitle: '',
                email: '',
                phone: '',
                error: '',
            },
        ])

        setDisabled(true)
    }

    // Remove the last entry
    const removeEntry = () => {
        if (entries.length > 1) {
            setEntries(
                entries.slice(0, -1).map((entry) => {
                    validateFormEntry(entry)
                    return entry
                })
            )
        }
    }

    const validateFormEntry = (entry: ReferenceAnswer) => {
        const schema = Yup.object().shape({
            firstName: Yup.string().required('First Name is required'),
            lastName: Yup.string().required('Last Name is required'),
            company: Yup.object().required('Company is required'),
            workTitle: Yup.string().required('Title is required'),
            email: Yup.string().required('Email is required'),
            phone: Yup.string()
                .test('phone', 'Please provide a valid phone number', (value) =>
                    isValidPhoneNumber(value || '')
                )
                .required('Phone number is required'),
        })

        schema
            .validate(entry, { abortEarly: true })
            .then(() => {
                entry.error = ''
                setDisabled(false)
            })
            .catch((error: Yup.ValidationError) => {
                entry.error = error.message
                setDisabled(true)
                return
            })

        setDisabled(false)
    }

    const updateEntry = (
        id: number,
        updatedEntry: Partial<ReferenceAnswer>
    ) => {
        setEntries(
            entries.map((entry) => {
                if (entry.id === id) {
                    const updated = { ...entry, ...updatedEntry }
                    validateFormEntry(updated)
                    return updated
                }
                validateFormEntry(entry)
                return entry
            })
        )
    }

    const submitData = () => {
        const response = entries
        callback({ response })
    }

    return (
        <>
            <IntakeCard
                question={prompt}
                skipCallback={skipCallback}
                skipText={skipText}
            >
                {!loading &&
                    Array.isArray(entries) &&
                    entries.map((entry, index) => (
                        <Container
                            style={{
                                display: 'inline-block',
                                alignContent: 'left',
                                marginBottom: getPxAsRem(36),
                                marginTop: '1rem',
                                padding: '0',
                                width: '100%',
                                maxWidth: '100%',
                            }}
                        >
                            <FormControl
                                style={{ marginBottom: '1.5rem' }}
                                fullWidth
                            >
                                <TextField
                                    id={`firstName_${entry.id}`}
                                    label="First Name"
                                    placeholder="First Name"
                                    value={entry.firstName}
                                    fullWidth
                                    onChange={(e) => {
                                        updateEntry(entry.id, {
                                            firstName: e.target.value,
                                        })
                                    }}
                                />
                            </FormControl>

                            <FormControl
                                style={{ marginBottom: '1.5rem' }}
                                fullWidth
                            >
                                <TextField
                                    id={`lastName_${entry.id}`}
                                    label="Last Name"
                                    placeholder="Last Name"
                                    value={entry.lastName}
                                    fullWidth
                                    onChange={(e) => {
                                        updateEntry(entry.id, {
                                            lastName: e.target.value,
                                        })
                                    }}
                                />
                            </FormControl>
                            <CompaniesAutocomplete
                                style={{ marginBottom: '1.5rem' }}
                                value={entry.company}
                                onChange={(e, nextValue) => {
                                    updateEntry(entry.id, {
                                        company: nextValue,
                                    })
                                }}
                            />
                            <FormControl
                                style={{ marginBottom: '1.5rem' }}
                                fullWidth
                            >
                                <InputLabel id={`title_input_${entry.id}`}>
                                    Title
                                </InputLabel>
                                <Select
                                    id={`title_${entry.id}`}
                                    labelId={`title_label_${entry.id}`}
                                    label="Title"
                                    value={entry.workTitle}
                                    onChange={(e) => {
                                        const workTitle =
                                            workTitles.find(
                                                (workTitle: string) =>
                                                    workTitle === e.target.value
                                            ) || ''
                                        updateEntry(entry.id, { workTitle })
                                    }}
                                >
                                    {workTitles.map((workTitle: string) => (
                                        <MenuItem
                                            key={workTitle}
                                            value={workTitle}
                                        >
                                            {workTitle}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                style={{ marginBottom: '1.5rem' }}
                                fullWidth
                            >
                                <TextField
                                    id={`email_${entry.id}`}
                                    label="Email"
                                    value={entry.email}
                                    onChange={(e) => {
                                        updateEntry(entry.id, {
                                            email: e.target.value,
                                        })
                                    }}
                                />
                            </FormControl>
                            <FormControl
                                style={{ marginBottom: '1.5rem' }}
                                fullWidth
                            >
                                <NonFormikPhoneInput
                                    id={`phone_${entry.id}`}
                                    size="small"
                                    value={entry.phone}
                                    error={
                                        entry.phone !== '' &&
                                        !isValidPhoneNumber(entry.phone)
                                            ? true
                                            : false
                                    }
                                    helperText={
                                        entry.phone !== '' &&
                                        !isValidPhoneNumber(entry.phone)
                                            ? 'Please provide valid phone no.'
                                            : ''
                                    }
                                    label="Phone"
                                    handleInputChange={(value) => {
                                        updateEntry(entry.id, {
                                            phone: value || '',
                                        })
                                    }}
                                />
                            </FormControl>
                        </Container>
                    ))}
                <AdditionalButtonsContainer>
                    {entries.length > 1 && (
                        <TertiaryButton
                            disabled={entries.length <= 1}
                            onClickHandler={removeEntry}
                            title={'Remove Reference'}
                            style={{ marginRight: '1rem' }}
                        />
                    )}
                    <SecondaryButton
                        title="Add Reference"
                        disabled={false}
                        onClickHandler={addEntry}
                    />
                </AdditionalButtonsContainer>
                <NextButton disabled={disabled} onClickHandler={submitData} />
            </IntakeCard>
        </>
    )
}

export default ReferenceQuestion
